import { useMemo } from 'react';
import {
  useCounterparties,
  useCustodians,
  useBenchmarks,
  toBenchmarkRateMap,
  toBenchmarkMetaMap,
} from '@/hooks';
import { useFxRatesSync } from '@/hooks/fx-rates';
import type { Counterparty } from '@/types/counterparty.types';

// A stable empty list: `counterparties` is a dep of the validation effect, and a fresh `[]` per
// render would re-arm it after Submit and wipe the server's errors.
const NO_COUNTERPARTIES: Counterparty[] = [];

/**
 * Shared trade-form reference data — the registries the create (useCreateTradeForm)
 * and amend (useAmendTradeForm) forms both need: counterparties, custodians, the
 * backend benchmark registry (rate + meta maps, LR-435/LR-443) and live FX sync.
 *
 * Fetches are gated on `isOpen` because the modals stay mounted in the Layout;
 * without the gate the full registries would fetch on every page (single source
 * for that gate, LR-495). `usePrivateSettings` stays in the create hook only — it
 * feeds legacy custodian-account prefixing, which amend does not do.
 */
export function useTradeFormReferenceData(isOpen: boolean) {
  const { isUsingFallback: isUsingFallbackRates } = useFxRatesSync();
  const {
    data: counterparties = NO_COUNTERPARTIES,
    isLoading: counterpartiesLoading,
    isError: counterpartiesError,
    refetch: refetchCounterparties,
  } = useCounterparties({ enabled: isOpen });
  const {
    data: custodians = [],
    isLoading: custodiansLoading,
    isError: custodiansError,
  } = useCustodians({ enabled: isOpen });
  const { data: benchmarkData } = useBenchmarks(isOpen);
  const benchmarkRates = useMemo(() => toBenchmarkRateMap(benchmarkData), [benchmarkData]);
  const benchmarkMeta = useMemo(() => toBenchmarkMetaMap(benchmarkData), [benchmarkData]);

  // LR-625: an in-flight or failed read also leaves the list empty, and telling
  // the trader "no registry rows" for either is the LR-572 lie. Counterparty is
  // a required field, so a failed read there blocks the booking outright — it
  // needs the same three-way answer as the custodian list.
  //
  // "Failed" means failed WITH NOTHING TO SHOW. These hooks refetch on window
  // focus (only the trade-form counterparty one opts out — see the kvi-r4 #1
  // note there), and Refresh below refetches all of them, so `isError` also
  // turns true when a REFRESH fails over rows that are still cached. Reporting
  // the failure then would caption a working dropdown with "the list is empty
  // because the registry failed" — the same false statement, mirrored.
  //
  // The two questions use DIFFERENT counts, on purpose:
  //  - "did the read fail with nothing to show" is about LOADED rows;
  //  - "is the dropdown empty" is about SELECTABLE rows. Both lists arrive
  //    already filtered (`is_active: true`), so the two coincide today; kept
  //    apart because the options still re-filter `isActive` (kvi review).
  // Hence `empty` is gated on `failed`, not on raw `isError`: with cached rows
  // that are all inactive the honest answer is "none active", not "the read
  // failed" — we have the data, the dropdown is empty for another reason.
  const selectableCounterparties = useMemo(
    () => counterparties.filter((cp) => cp.isActive).length,
    [counterparties],
  );
  const custodiansFailed = custodiansError && custodians.length === 0;
  const counterpartiesFailed = counterpartiesError && counterparties.length === 0;
  const custodiansEmpty = !custodiansLoading && !custodiansFailed && custodians.length === 0;
  const counterpartiesEmpty =
    !counterpartiesLoading && !counterpartiesFailed && selectableCounterparties === 0;

  return {
    isUsingFallbackRates,
    counterparties,
    custodians,
    custodiansEmpty,
    custodiansError: custodiansFailed,
    counterpartiesEmpty,
    counterpartiesError: counterpartiesFailed,
    refetchCounterparties,
    benchmarkRates,
    benchmarkMeta,
  };
}
